// Feature: business-coverage-from-quote
// Request/response contracts for the "Generate Business Coverage Summary from
// Quote" flow consumed from the backend endpoint
// `POST /policy/generate-business-coverage-from-quote`.
//
// Bucket fields on the raw response are JSON-stringified `ICoverageRow[]`
// (the server serializes them before returning). Any downstream consumer that
// feeds the Coverage edit form must `JSON.parse` them first.

/** Which of the three mutually exclusive quote sources the user picked. */
export type CoverageFromQuoteSource = "UPLOAD" | "MARKETING" | "S3_KEY";

/** Arguments accepted by the `generateCoverageFromQuoteAsync` thunk. */
export interface IGenerateCoverageFromQuoteArgs {
  source: CoverageFromQuoteSource;
  /** Required when `source === "UPLOAD"`. */
  file?: File | null;
  /** Required when `source === "S3_KEY"`. */
  quote_s3_key?: string | null;
  /** Required when `source === "MARKETING"`. */
  marketing_serial_number?: number | null;
  /** Linkage A — one of `customer_policy_id` / `lead_id` is required. */
  customer_policy_id?: number | null;
  /** Linkage B — takes precedence over `customer_policy_id` when both set. */
  lead_id?: string | null;
  /** Optional override; AI-extracted value wins if omitted. */
  risk_location?: number | null;
  /** Optional; backend defaults to `"MARKETING_QUOTE"`. */
  created_from?: string;
  /** Optional; backend defaults to the marketing serial or first s3 key. */
  reference_form_id?: string | null;
}

/** OpenAI token usage echoed back by the backend. */
export interface ICoverageFromQuoteUsage {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
}

/**
 * Raw server response under `res.data.data`. Bucket fields inside
 * `new_coverage` are JSON strings (see module docstring).
 */
export interface IGenerateCoverageFromQuoteRaw {
  inactivated_coverage_numbers: number[];
  new_coverage_number: number;
  new_coverage: Record<string, unknown>;
  schema_match: boolean;
  model: string;
  usage: ICoverageFromQuoteUsage;
  files: string[];
}

/** Dialog lifecycle stage, driven entirely by the thunk lifecycle. */
export type GenerateFromQuoteStage = "idle" | "running" | "done" | "error";

export interface ICoverageFromQuoteState {
  stage: GenerateFromQuoteStage;
  source: CoverageFromQuoteSource;
  result: IGenerateCoverageFromQuoteRaw | null;
  error: string | null;
}

export const defaultCoverageFromQuoteState: ICoverageFromQuoteState = {
  stage: "idle",
  source: "UPLOAD",
  result: null,
  error: null,
};
